import { useState, useReducer, useEffect } from "react";
import { Dialog, DialogTitle } from "@mui/material";
import isEqual from "lodash/isEqual";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosInstance } from "axios";
import { useApi } from "@platform/api";

import ConfirmDialog from "../../components/confirmDialog";
import { formatDate, getClientPayload } from "../../utils";
import {
  useToastNotificationService,
  usePrepareClientFormState,
} from "../../hooks";
import {
  CLIENT_FORM,
  CLIENT_TYPES,
  CLIENT_REDUCER_ACTIONS,
  IS_CLIENT_FORM_VALID,
  CUSTOM_CONF_FIELDS,
} from "../../constants/client";
import reducer, { initState } from "../../reducers/client";
import { Client } from "../../types/clientTab";

import ClientDialogContent from "./clientDialogContent";
import { getClientQueryKey } from "./utils";

const { UPDATE_CLIENT, CLEAN_CLIENT } = CLIENT_REDUCER_ACTIONS;
const { CLIENT_SINCE, CLIENT_TYPE, CLIENT_ID, STATE, NAME, SITUS_STATE } =
  CLIENT_FORM;

interface Props {
  client?: Client;
  onClose: VoidFunction;
}

type ClientPayload = Record<string, unknown>;
type ClientFormState = Record<string, unknown>;
type ClientFormAction = {
  type: string;
  value?: ClientFormState;
  field?: string;
};

function EditClientDialog({
  client,
  onClose,
}: Props): React.JSX.Element | null {
  const api: AxiosInstance = useApi();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { createToastNotification } = useToastNotificationService();
  const [clientForm, setClientForm] = useReducer(
    reducer as (
      state: ClientFormState,
      action: ClientFormAction,
    ) => ClientFormState,
    initState as ClientFormState,
  );
  const [originalClient, setOriginalClient] = useState<ClientPayload | null>(
    null,
  );
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const prepareClientFormState = usePrepareClientFormState(!!client);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/brokerportal/v1/clients/${client.clientId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getClientQueryKey(client.clientId),
      });
      createToastNotification({
        severity: "success",
        message: "Client successfully updated!",
      });

      handleClose();
    },
    onError: () => {
      createToastNotification({
        severity: "error",
        message: "Error updating client. Please try again later!",
      });
    },
  });

  function getPayload(): ClientPayload {
    return getClientPayload(
      {
        ...clientForm,
        [STATE]: (clientForm[STATE] as { abbreviation?: string } | undefined)
          ?.abbreviation,
        [SITUS_STATE]: (
          clientForm[SITUS_STATE] as { abbreviation?: string } | undefined
        )?.abbreviation,
        [CLIENT_SINCE]: formatDate(
          clientForm[CLIENT_SINCE] as Date | undefined,
        ),
      },
      prepareClientFormState.data?.brokerClientInfoConfig,
    ) as ClientPayload;
  }

  function handleClose(): void {
    setClientForm({ type: CLEAN_CLIENT });
    setOriginalClient(null);
    onClose();
  }

  function handleDialogClose(): void {
    if (mutation.isPending) return;

    const initPayload = getPayload();
    if (isEqual(initPayload, originalClient)) {
      handleClose();
      return;
    }
    setIsOpenConfirmDialog(true);
  }

  function handleSubmit(): void {
    if (!clientForm[IS_CLIENT_FORM_VALID]) return;

    const payload = getPayload();
    if (isEqual(payload, originalClient)) {
      handleClose();
      return;
    }

    mutation.mutate({
      ...payload,
      isProspect: false,
      [CLIENT_ID]: clientForm[CLIENT_ID],
    });
  }

  useEffect(() => {
    if (!client || !prepareClientFormState.data) return;

    /* eslint-disable @typescript-eslint/no-unsafe-assignment */
    setClientForm({
      type: UPDATE_CLIENT,
      value: {
        ...client,
        [CLIENT_SINCE]: client[CLIENT_SINCE]
          ? new Date(client[CLIENT_SINCE])
          : undefined,
        [CLIENT_TYPE]: CLIENT_TYPES.CURRENT_CLIENT,
        states: prepareClientFormState.data.states,
        [CUSTOM_CONF_FIELDS]:
          prepareClientFormState.data.brokerClientInfoConfig,
      } as ClientFormState,
    });
    setOriginalClient(
      getClientPayload(
        {
          ...client,
          ...client.address,
          ...client.additionalInfo,
          [CLIENT_SINCE]: client[CLIENT_SINCE],
        },
        prepareClientFormState.data.brokerClientInfoConfig,
      ) as ClientPayload,
    );
    /* eslint-enable @typescript-eslint/no-unsafe-assignment */
  }, [client, prepareClientFormState.data]);

  return (
    <>
      <Dialog
        open={!!client}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Client Information</DialogTitle>
        <ClientDialogContent
          submitText="SAVE"
          disabled={!clientForm[IS_CLIENT_FORM_VALID]}
          isSubmit={mutation.isPending}
          clientForm={clientForm}
          setClientForm={setClientForm}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          prepareClientFormState={prepareClientFormState}
          onSubmit={handleSubmit}
          onCancel={handleDialogClose}
        />
      </Dialog>
      <ConfirmDialog
        isOpen={isOpenConfirmDialog}
        title="Confirm cancellation"
        message={`Are you sure you want to cancel editing ${originalClient?.[NAME]}?`}
        confirmBtnMsg="YES, CANCEL"
        closeBtnMsg="NO"
        onClose={() => setIsOpenConfirmDialog(false)}
        onConfirm={() => {
          setIsOpenConfirmDialog(false);
          handleClose();
        }}
      />
    </>
  );
}

export default EditClientDialog;
